/**
 * PUTTING A JOB ON A MAP, FROM THE WORDS SOMEBODY TYPED.
 *
 * These rules were written for the public service map and lived inside
 * `server/routes/map.ts`. The desk's own day map needs exactly the same
 * rules, and a second copy of them would drift within a week — the two
 * maps would disagree about where Hemet is, and only one of them would be
 * wrong at a time. So they moved here, to the shared layer, and the server
 * imports them like everything else in `src/domain`.
 *
 * Nothing here talks to a network or a database. Give it a string and a
 * table of centroids and it either names a place or admits it cannot.
 *
 * THE ONE RULE WORTH KEEPING IN MIND: a place is NAMED, never inferred. A
 * town with no row in the centroid table comes back null and gets counted
 * as unplaced. Guessing is how "27213 Cabrillo Drive" once read as a North
 * Carolina zip and put Hemet in Tampa.
 */

export type Centroid = [number, number];
export type CentroidTable = Record<string, Centroid>;

/**
 * EVERY STATE, NOT JUST THE WEST.
 *
 * This used to list California, Arizona, Nevada, Oregon, Washington, Texas,
 * Ohio and Pennsylvania and nothing else, because that is where the work
 * was. Then R&T's Lock and Key in Auburn, Alabama signed up, and Anthony
 * asked, 2026-10-13: "Place Rob's jobs as well please".
 *
 * An Alabama address failed in the worst way, which is quietly and in the
 * wrong place. ", AL" was not a state here, so the comma parse returned
 * "AL" as the town, found no row, and fell through to the street scan.
 * "500 Highland Ave, Montgomery, AL" then matched Highland, California
 * by its STREET NAME and drew a pin two thousand miles from the job. The
 * same thing put "Portland, ME" in Oregon. A pin in the wrong state never
 * reaches the national placer, because only an unplaced call is sent.
 *
 * So every postal code and every state name is listed, plus DC, and a
 * string that names a state other than California skips the local table
 * entirely (see place() below).
 */
export const US_STATES: Record<string, string> = {
  alabama: "al", alaska: "ak", arizona: "az", arkansas: "ar", california: "ca",
  colorado: "co", connecticut: "ct", delaware: "de", florida: "fl", georgia: "ga",
  hawaii: "hi", idaho: "id", illinois: "il", indiana: "in", iowa: "ia",
  kansas: "ks", kentucky: "ky", louisiana: "la", maine: "me", maryland: "md",
  massachusetts: "ma", michigan: "mi", minnesota: "mn", mississippi: "ms",
  missouri: "mo", montana: "mt", nebraska: "ne", nevada: "nv",
  "new hampshire": "nh", "new jersey": "nj", "new mexico": "nm", "new york": "ny",
  "north carolina": "nc", "north dakota": "nd", ohio: "oh", oklahoma: "ok",
  oregon: "or", pennsylvania: "pa", "rhode island": "ri", "south carolina": "sc",
  "south dakota": "sd", tennessee: "tn", texas: "tx", utah: "ut", vermont: "vt",
  virginia: "va", washington: "wa", "west virginia": "wv", wisconsin: "wi",
  wyoming: "wy", "district of columbia": "dc", "washington dc": "dc",
  calif: "ca",
};
const STATE_CODES = new Set(Object.values(US_STATES));

/** "AL", "al.", "Alabama", "Calif." -> "al"/"ca". Anything else -> null. */
export function stateCode(word: unknown): string | null {
  const s = String(word ?? "").trim().toLowerCase().replace(/\.$/, "").replace(/\s+/g, " ");
  if (!s) return null;
  if (s === "d.c" || s === "d. c") return "dc";
  if (STATE_CODES.has(s)) return s;
  return US_STATES[s] ?? null;
}

/** A US state sitting on its own at the end of a segment. */
const isStateTail = (seg: string): boolean => stateCode(seg) !== null;

/**
 * Two-letter codes that are also everyday English words. A lower-case
 * "in" or "me" at the end of free text is a word, not a state; only a
 * whole comma segment, or the code written in capitals, counts for these.
 */
const WORDLIKE = new Set(["in", "or", "me", "hi", "ok", "oh", "la", "de", "id", "ma", "pa", "co", "mo", "ne", "al", "wa", "ga", "md", "va"]);

/**
 * THE STATE A STRING NAMES, IF IT NAMES ONE, read only where a state goes:
 * at the end, before any zip. "..., Auburn, AL 36832", "Montgomery AL",
 * "Hemet CA, 92545" and "..., Opelika, Alabama" all answer. A state name
 * in the middle of a street ("2712 Alabama 69") does not.
 */
export function namedState(text: unknown): string | null {
  const parts = String(text ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const seg = (parts[i] ?? "").replace(/\s*\b\d{5}(-\d{4})?$/, "").trim();
    if (!seg) continue;                       // the segment was only a zip
    const whole = stateCode(seg);
    if (whole && (parts.length > 1 || seg.length > 2)) return whole;
    const words = seg.split(/\s+/);
    if (words.length < 2) return null;
    const two = stateCode(words.slice(-2).join(" "));
    if (two && words.length > 2 && words.slice(-2).join(" ").length > 2) return two;
    const last = words[words.length - 1] ?? "";
    const code = stateCode(last);
    if (!code) return null;
    const bare = last.replace(/\.$/, "");
    if (bare.length === 2 && WORDLIKE.has(bare.toLowerCase()) && bare !== bare.toUpperCase()) return null;
    return code;
  }
  return null;
}

/**
 * The state the LOCAL tables below belong to. city-centroids.json is
 * Supercanic's own corner of the country; a string that names any other
 * state is the national placer's question, not this file's.
 */
const LOCAL_STATE = "ca";

/**
 * "LA" at the end of a Southern California address is Los Angeles far more
 * often than it is Louisiana ("4642 W 142nd St, Hawthorne, LA"). So for a
 * shop that uses the local table, "LA" alone does not push a string off
 * it; a Louisiana shop never reaches the local table (localTableFits).
 */
const LA_IS_A_TOWN = "la";

/**
 * MAY THIS SHOP USE THE LOCAL TABLE AT ALL?
 *
 * A shop whose own address names another state does not get California's
 * towns read into its jobs. "Highland" or "Corona" written on an Alabama
 * locksmith's job is a street or a namesake, never the California town.
 * Unknown (no address, or one that names no state) keeps today's
 * behaviour, so nothing that places now can move.
 */
export function localTableFits(shopAddress: unknown): boolean {
  const st = namedState(shopAddress);
  return !st || st === LOCAL_STATE;
}

/**
 * "In the shop" is not a missing address. It is the most precise one on the
 * record: the work happened at 1685 S State St, San Jacinto.
 */
export const IN_SHOP = /^\s*(in the shop|at the shop|in shop|shop)\s*$/i;
export const SHOP_CITY = "san jacinto";

/** The city out of a location string: the last comma segment that is not a state. */
export function cityFrom(location: unknown): string | null {
  const parts = String(location ?? "").split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  // Walk back past a trailing state and past a bare zip, so
  // "...,  Hemet, CA 92543" and "..., Hemet, CA" both land on Hemet.
  for (let i = parts.length - 1; i >= 1; i--) {
    const seg = (parts[i] ?? "").replace(/\s+\d{5}(-\d{4})?$/, "").trim();
    if (!seg) continue;
    if (isStateTail(seg)) continue;
    if (/^\d{5}(-\d{4})?$/.test(seg)) continue;
    return seg;
  }
  return null;
}

/**
 * The centroid table's keys are lowercase and this string is what a popup
 * shows, so without this a pin reading "riverside" would sit next to one
 * reading "Grand Terrace".
 */
function titleCase(name: string): string {
  return name.replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}

/**
 * A KNOWN TOWN NAMED ANYWHERE IN THE STRING.
 *
 * "575 Oakland st Hemet" and "3945 pierce st #653 riverside 92505" have no
 * comma to parse, but they do say where they are. This matches only against
 * the towns already in the centroid table, longest name first so Lake
 * Elsinore is not read as Elsinore, and on whole words. The comma parse
 * has already had its turn by the time this runs, so a street named after
 * another town cannot outrank a stated city.
 *
 * The sorted key list is cached PER TABLE rather than once for the module.
 * The old version cached the first table it ever saw, which was harmless
 * while one process had one table and would have been a silent wrong
 * answer the moment a second one appeared.
 */
const sortedKeys = new WeakMap<CentroidTable, string[]>();

export function knownCityIn(text: unknown, table: CentroidTable): string | null {
  const s = String(text ?? "").toLowerCase();
  if (!s) return null;
  let names = sortedKeys.get(table);
  if (!names) {
    names = Object.keys(table).sort((a, b) => b.length - a.length);
    sortedKeys.set(table, names);
  }
  for (const name of names) {
    if (name.length < 4) continue;
    const at = s.indexOf(name);
    if (at < 0) continue;
    const before = at === 0 ? " " : s[at - 1] ?? " ";
    const after = s[at + name.length] ?? " ";
    if (/[a-z]/.test(before) || /[a-z]/.test(after)) continue;
    return titleCase(name);
  }
  return null;
}

/**
 * A ZIP ONLY WHERE A ZIP GOES: at the end, after the city.
 *
 * This once matched any five digits anywhere, and 549 of these locations
 * START with a five-digit street number.
 */
export const zipFrom = (s: unknown): string =>
  (String(s ?? "").match(/\b(\d{5})(?:-\d{4})?\s*$/) ?? [])[1] ?? "";

export interface Placed {
  at: Centroid;
  /** The town, as it should be read out. Null when only a zip placed it. */
  city: string | null;
}

/**
 * PLACE ONE STRING, four ways, cheapest first, never guessing.
 *
 * The shop's own address, then the comma parse, then a known town named
 * anywhere in the string, then a trailing zip. `zips` is optional because
 * the day map does not carry the 49KB zip table into a browser for the
 * thirteen historical rows that need it.
 */
export function place(
  text: string | null | undefined,
  cities: CentroidTable,
  zips?: CentroidTable,
): Placed | null {
  if (!text) return null;
  const centre = (city: string | null): Centroid | null =>
    cities[String(city ?? "").trim().toLowerCase()] ?? null;

  if (IN_SHOP.test(text)) {
    const at = centre(SHOP_CITY);
    return at ? { at, city: titleCase(SHOP_CITY) } : null;
  }

  /* Another state named outright: the California table must not answer.
     Its street scan is what put Montgomery, Alabama in Highland, CA. A
     null here sends the call to the national placer, which reads the
     city WITH its state. */
  const st = namedState(text);
  if (st && st !== LOCAL_STATE && st !== LA_IS_A_TOWN) return null;

  const named = cityFrom(text);
  const byName = centre(named);
  if (byName) return { at: byName, city: named };

  const scanned = knownCityIn(text, cities);
  const byScan = centre(scanned);
  if (byScan) return { at: byScan, city: scanned };

  const byZip = zips?.[zipFrom(text)] ?? null;
  if (byZip) return { at: byZip, city: named };

  return null;
}

/**
 * SPREAD PINS THAT SHARE A TOWN, WITHOUT MOVING THEM ON EVERY RELOAD.
 *
 * Every job in Hemet resolves to the same centroid, so eight of today's
 * calls would stack into one pin and read as one. The public map solves
 * this with a random scatter, which is right there — the randomness IS the
 * privacy. This map is behind the sign-in and shows the shop its own work,
 * so there is nothing to hide and a pin that jumps two kilometers every
 * time the page redraws is just a pin you cannot point at.
 *
 * So the angle and the radius come from the job's id instead of from
 * Math.random: the same job lands in the same spot today, tomorrow, and on
 * the screen next to yours. Radius is square-rooted so points spread evenly
 * over the disc rather than bunching in the middle, and longitude is
 * divided by cos(latitude) so the cloud is round on the map rather than
 * squashed east to west.
 *
 * A KILOMETER, not the public map's two. This is a town-level centroid
 * either way — the scatter is here to separate pins, not to claim accuracy
 * the address never had.
 */
const SPREAD_KM = 1.0;

function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967296;
}

export function scatter(at: Centroid, seed: string): Centroid {
  const [lat, lng] = at;
  const angle = hash(seed) * Math.PI * 2;
  const radiusKm = Math.sqrt(hash(`${seed}:r`)) * SPREAD_KM;
  const dLat = (radiusKm / 111) * Math.cos(angle);
  const dLng = (radiusKm / (111 * Math.cos((lat * Math.PI) / 180))) * Math.sin(angle);
  return [lat + dLat, lng + dLng];
}
